/**
 * Localization strings used for Interactions
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const interactionsText = createDictionary({
  interactions: {
    'en-us': 'Interactions',
    'ru-ru': 'Взаимодействия',
    'es-es': 'Interacciones',
    'fr-fr': 'Interactions',
    'uk-ua': 'Взаємодії',
    'de-ch': 'Wechselwirkungen',
    'pt-br': 'Interações',
    'hr-hr': 'Interakcije',
    nb: 'Interaksjoner',
  },
  addItems: {
    'en-us': 'Add Items',
    'ru-ru': 'Добавить элементы',
    'es-es': 'Agregar elementos',
    'fr-fr': 'Ajouter des objets',
    'uk-ua': 'Додати елементи',
    'de-ch': 'Elemente hinzufügen',
    'pt-br': 'Adicionar itens',
    'hr-hr': 'Dodaj stavke',
    nb: 'Legg til elementer',
  },
  recordReturn: {
    'en-us': '{table:string} Return',
    'ru-ru': '{table:string} Возврат',
    'es-es': '{table:string} Regresar',
    'fr-fr': '{table:string} Retour',
    'uk-ua': '{table:string} Повернення',
    'de-ch': '{table:string} Rückkehr',
    'pt-br': '{table:string} Retornar',
    'hr-hr': '{table:string} Povratak',
    nb: '{table:string} Tilbake',
  },
  noInteractions: {
    comment: 'Example: There are no interactions linked to this {preparation}',
    'en-us': 'There are no interactions linked to this preparation.',
    'de-ch': 'Mit dieser Zubereitung sind keine Wechselwirkungen verbunden.',
    'fr-fr': "Aucune interaction n'est liée à cette préparation.",
    'hr-hr': 'Nema interakcija povezanih s ovim pripravkom.',
    'pt-br': 'Não existem interações associadas a esta preparação.',
    'ru-ru': 'Взаимодействия с данным препаратом не выявлены.',
    'uk-ua': "Немає жодних взаємодій, пов'язаних з цим препаратом.",
    'es-es': 'No existen interacciones relacionadas con esta preparación.',
    nb: 'Det er ingen interaksjoner knyttet til dette preparatet.',
  },
  tableLabelRecords: {
    comment: 'Example: Loan records',
    'en-us': '{tableLabel:string} records',
    'ru-ru': '{tableLabel:string} записи',
    'es-es': '{tableLabel:string} registros',
    'fr-fr': '{tableLabel:string} enregistrements',
    'uk-ua': '{tableLabel:string} записи',
    'de-ch': '{tableLabel:string} Datensätze',
    'pt-br': '{tableLabel:string} registros',
    'hr-hr': '{tableLabel:string} zapisi',
    nb: '{tableLabel:string} poster',
  },
  preparationsNotFoundFor: {
    'en-us': 'No preparations were found for the following records:',
    'de-ch':
      'Für die folgenden Datensätze wurden keine Vorbereitungen gefunden:',
    'es-es': 'No se encontraron preparaciones para los siguientes registros:',
    'fr-fr':
      "Aucune préparation n'a été trouvée pour les enregistrements suivants :",
    'ru-ru':
      'Не было обнаружено никаких подготовительных работ для следующих записей:',
    'uk-ua': 'Для наступних записів не знайдено жодних підготовчих матеріалів:',
    'pt-br': 'Não foram encontradas preparações para os seguintes registros:',
    'hr-hr': 'Nisu pronađene pripreme za sljedeće zapise:',
    nb: 'Ingen forberedelser ble funnet for følgende poster:',
  },
  preparationsNotAvailableFor: {
    'en-us':
      'No preparations are available for at least one type of preparation in the following records:',
    'de-ch':
      'Für mindestens eine Zubereitungsart sind in den folgenden Datensätzen keine Präparate verfügbar:',
    'es-es':
      'No hay preparaciones disponibles para al menos un tipo de preparación en los folgenden registros:',
    'fr-fr':
      "Aucune préparation n'est disponible pour au moins un type de préparation dans les enregistrements suivants :",
    'ru-ru':
      'В следующих записях отсутствуют данные как минимум об одном из видов препаратов:',
    'uk-ua':
      'У наступних записах відсутні препарати принаймні для одного типу препарату:',
    'pt-br':
      'Não há preparações disponíveis para pelo menos um tipo de preparação nos seguintes registros:',
    'hr-hr':
      'U sljedećim zapisima nisu dostupni pripravci za barem jednu vrstu pripravka:',
    nb: 'Ingen preparater er tilgjengelige for minst én type preparat i følgende poster:',
  },
  problemsFound: {
    'en-us': 'There are problems with the entry:',
    'ru-ru': 'В записи обнаружены ошибки:',
    'es-es': 'Hay problemas con la entrada:',
    'fr-fr': 'Il y a des problèmes avec la saisie :',
    'uk-ua': 'Є проблеми зі вступом:',
    'de-ch': 'Es gibt Probleme mit dem Eintrag:',
    'pt-br': 'Existem problemas com a entrada:',
    'hr-hr': 'Postoje problemi s unosom:',
    nb: 'Det er problemer med inntastingen:',
  },
  byChoosingRecordSet: {
    comment: 'See documentation for syntax for plural rules',
    'en-us': 'Choose a {recordSet:string} ({count:number|formatted} available)',
    'de-ch':
      'Wählen Sie ein {recordSet:string} ({count:number|formatted} verfügbar)',
    'es-es':
      'Elija un {recordSet:string} ({count:number|formatted} disponible)',
    'fr-fr':
      'Choisissez un {recordSet:string} ({count:number|formatted} disponible)',
    'pt-br':
      'Escolha um {recordSet:string} ({count:number|formatted} disponível)',
    'ru-ru': 'Выберите {recordSet:string} ({count:number|formatted} доступно)',
    'uk-ua': 'Виберіть {recordSet:string} (доступно {count:number|formatted})',
    'hr-hr':
      'Odaberite {recordSet:string} (dostupno je {count:number|formatted})',
    nb: 'Velg en {recordSet:string} ({count:number|formatted} tilgjengelig)',
  },
  byEnteringNumbers: {
    comment: `
      Field name is localized. Coming from Schema Configuration. I.e, By
      entering Catalog Numbers
    `,
    'en-us': 'Enter {tableName:string} {fieldName:string}s',
    'de-ch': '{tableName:string} {fieldName:string}s eingeben',
    'es-es': 'Introduzca {tableName:string} {fieldName:string}s',
    'fr-fr': 'Saisir des {fieldName:string}s de {tableName:string}',
    'ru-ru': 'Введите {tableName:string} {fieldName:string}s',
    'uk-ua': 'Введіть {fieldName:string} {tableName:string}',
    'pt-br': 'Digite {tableName:string} {fieldName:string}s',
    'hr-hr': 'Unesite {tableName:string} {fieldName:string}s',
    nb: 'Skriv inn {tableName:string} {fieldName:string}',
  },
  enterLoanNumbers: {
    'en-us': 'Enter {fieldName: string}s',
    'de-ch': 'Geben Sie {fieldName: string}s ein',
    'es-es': 'Ingrese {fieldName: string}s',
    'fr-fr': 'Saisir des {fieldName: string}s',
    'ru-ru': 'Введите {fieldName: string}s',
    'uk-ua': 'Введіть {fieldName: string}s',
    'pt-br': 'Insira {fieldName: string}s',
    'hr-hr': 'Unesite {fieldName: string}',
    nb: 'Skriv inn {fieldName: string}',
  },
  withoutPreparations: {
    'en-us': 'Without preparations',
    'ru-ru': 'Без подготовки',
    'es-es': 'Sin preparaciones',
    'fr-fr': 'Sans préparations',
    'uk-ua': 'Без підготовки',
    'de-ch': 'Ohne Präparate',
    'pt-br': 'Sem preparativos',
    'hr-hr': 'Bez priprema',
    nb: 'Uten forberedelser',
  },
  continueWithoutPreparations: {
    'en-us': 'Continue without preparations',
    'de-ch': 'Ohne Vorbereitungen fortfahren',
    'es-es': 'Continuar sin preparaciones',
    'fr-fr': 'Continuer sans préparation',
    'ru-ru': 'Продолжать без подготовки',
    'uk-ua': 'Продовжуйте без підготовки',
    'pt-br': 'Continuar sem preparativos',
    'hr-hr': 'Nastavite bez pripreme',
    nb: 'Fortsett uten forberedelser',
  },
  addUnassociated: {
    'en-us': 'Add unassociated item',
    'ru-ru': 'Добавить несвязанный элемент',
    'es-es': 'Agregar elemento no asociado',
    'fr-fr': 'Ajouter un objet non associé',
    'uk-ua': "Додати непов'язаний елемент",
    'de-ch': 'Nicht assoziierter Gegenstand hinzufügen',
    'pt-br': 'Adicionar item não associado',
    'hr-hr': 'Dodaj nepovezanu stavku',
    nb: 'Legg til et ikke-tilknyttet objekt',
  },
  preparations: {
    'en-us': 'Preparations',
    'ru-ru': 'Подготовка',
    'es-es': 'Preparaciones',
    'fr-fr': 'Preparations',
    'uk-ua': 'Підготовка',
    'de-ch': 'Präparate',
    'pt-br': 'Preparativos',
    'hr-hr': 'Pripreme',
    nb: 'Forberedelser',
  },
  preparationsCanNotBeReturned: {
    'en-us': 'Preparations cannot be returned in this context.',
    'ru-ru': 'В данном контексте возврат подготовленных материалов невозможен.',
    'es-es': 'En este contexto no es posible retomar las preparaciones',
    'fr-fr': 'Les preparations ne peuvent être renvoyées dans ce contexte.',
    'uk-ua': 'У цьому контексті препарати не можна повернути.',
    'de-ch': 'Präparate können in diesem Kontext nicht zurückgegeben werden.',
    'pt-br': 'Neste contexto, as preparações não podem ser devolvidas.',
    'hr-hr': 'Pripreme se u ovom kontekstu ne mogu vratiti.',
    nb: 'Preparater kan ikke returneres i denne sammenhengen.',
  },
  noUnresolvedPreparations: {
    'en-us': 'There are no unresolved preparations for this loan.',
    'ru-ru':
      'В настоящее время никаких незавершенных подготовительных работ по данному кредиту не требуется.',
    'es-es': 'No existen preparaciones pendientes para este préstamo.',
    'fr-fr': 'Il n’y a pas de preparations non retournées pour ce prêt.',
    'uk-ua':
      'Немає жодних невирішених питань щодо підготовки до цього кредиту.',
    'de-ch': 'Für dieses Ausleihe bestehen keine ungelösten Vorbereitungen.',
    'pt-br': 'Não há pendências processuais relativas a este empréstimo.',
    'hr-hr': 'Nema neriješenih priprema za ovaj kredit.',
    nb: 'Det er ingen uavklarte forberedelser for dette lånet.',
  },
  unresolved: {
    'en-us': 'Unresolved',
    'ru-ru': 'Не решено',
    'es-es': 'Sin resolver',
    'fr-fr': 'Non résolu',
    'uk-ua': 'Невирішено',
    'de-ch': 'Ungelöst',
    'pt-br': 'Não resolvido',
    'hr-hr': 'Neriješeno',
    nb: 'Uløst',
  },
  return: {
    comment: 'Verb',
    'en-us': 'Return',
    'ru-ru': 'Возвращаться',
    'es-es': 'Devolver',
    'fr-fr': 'Retour',
    'uk-ua': 'Повернення',
    'de-ch': 'Rückgabe',
    'pt-br': 'Retornar',
    'hr-hr': 'Povratak',
    nb: 'Tilbake',
  },
  resolve: {
    comment: 'As in "Resolve preparations"',
    'en-us': 'Resolve',
    'ru-ru': 'Решать',
    'es-es': 'Resolver',
    'fr-fr': 'Résoudre',
    'uk-ua': 'Вирішити',
    'de-ch': 'Lösen',
    'pt-br': 'Resolver',
    'hr-hr': 'Odlučnost',
    nb: 'Løse',
  },
  returnAllPreparations: {
    'en-us': 'Return all preparations',
    'ru-ru': 'Верните все приготовления',
    'es-es': 'Devuelva todos los preparativos.',
    'fr-fr': 'Retourner toutes les préparations',
    'uk-ua': 'Поверніть усі заготовки',
    'de-ch': 'Alle Präparate zurückgeben',
    'pt-br': 'Devolva todos os preparativos.',
    'hr-hr': 'Vratite sve pripreme',
    nb: 'Returner alle forberedelser',
  },
  returnSelectedPreparations: {
    'en-us': 'Return selected preparations',
    'ru-ru': 'Верните выбранные препараты',
    'es-es': 'Devolver preparaciones seleccionadas',
    'fr-fr': 'Retourner les préparations sélectionnées',
    'uk-ua': 'Повернути вибрані препарати',
    'de-ch': 'Ausgewählte Präparate zurückgeben',
    'pt-br': 'Devolva as preparações selecionadas',
    'hr-hr': 'Vrati odabrane pripreme',
    nb: 'Returner valgte preparater',
  },
  selectAllAvailablePreparations: {
    'en-us': 'Select all available preparations',
    'ru-ru': 'Выберите все доступные варианты приготовления.',
    'es-es': 'Seleccione todas las preparaciones disponibles',
    'fr-fr': 'Sélectionnez toutes les préparations disponibles',
    'uk-ua': 'Виберіть усі доступні препарати',
    'de-ch': 'Alle verfügbaren Präparate auswählen',
    'pt-br': 'Selecione todas as preparações disponíveis.',
    'hr-hr': 'Odaberite sve dostupne pripreme',
    nb: 'Velg alle tilgjengelige preparater',
  },
  selectAll: {
    'en-us': 'Select All',
    'ru-ru': 'Выбрать все',
    'es-es': 'Seleccionar todo',
    'fr-fr': 'Tout sélectionner',
    'uk-ua': 'Вибрати все',
    'de-ch': 'Alle auswählen',
    'pt-br': 'Selecionar tudo',
    'hr-hr': 'Odaberi sve',
    nb: 'Velg alle',
  },
  selectedAmount: {
    'en-us': 'Selected Amount',
    'ru-ru': 'Выбранная сумма',
    'es-es': 'Cantidad seleccionada',
    'fr-fr': 'Quantité sélectionnée',
    'uk-ua': 'Вибрана сума',
    'de-ch': 'Ausgewählte Anzahl',
    'pt-br': 'Valor selecionado',
    'hr-hr': 'Odabrani iznos',
    nb: 'Valgt beløp',
  },
  returnedAmount: {
    'en-us': 'Returned Amount',
    'ru-ru': 'Возвращенная сумма',
    'es-es': 'Cantidad devuelta',
    'fr-fr': 'Quantité retournée',
    'uk-ua': 'Повернена сума',
    'de-ch': 'Zurückgegebene Anzahl',
    'pt-br': 'Valor devolvido',
    'hr-hr': 'Vraćeni iznos',
    nb: 'Returnert antall',
  },
  resolvedAmount: {
    'en-us': 'Resolved Amount',
    'ru-ru': 'Решенная сумма',
    'es-es': 'Cantidad resuelta',
    'fr-fr': 'Montant résolu',
    'uk-ua': 'Вирішена сума',
    'de-ch': 'Gelöste Anzahl',
    'pt-br': 'Valor Resolvido',
    'hr-hr': 'Riješeni iznos',
    nb: 'Løst antall',
  },
  prepReturnFormatter: {
    comment: 'Used to format preparations in the prep return dialog',
    'en-us': '{tableName:string}: {resource:string}',
    'ru-ru': '{tableName:string}: {resource:string}',
    'es-es': '{tableName:string}: {resource:string}',
    'fr-fr': '{tableName:string} : {resource:string}',
    'uk-ua': "{tableName:string}': {resource:string}",
    'de-ch': '{tableName:string}: {resource:string}',
    'pt-br': '{tableName:string}: {resource:string}',
    'hr-hr': '{tableName:string}: {resource:string}',
    nb: '{tableName:string}: {resource:string}',
  },
  unCataloged: {
    'en-us': 'uncataloged',
    'ru-ru': 'некаталогизированный',
    'es-es': 'no catalogado',
    'fr-fr': 'non catalogué',
    'uk-ua': 'некаталогізований',
    'de-ch': 'nicht katalogisiert',
    'pt-br': 'não catalogado',
    'hr-hr': 'nekatalogizirano',
    nb: 'uregistert',
  },
  returnedPreparations: {
    comment: 'Example: Preparation records',
    'en-us': 'Returned {tablePreparation:string} records',
    'es-es': 'Registros devueltos {tablePreparation:string}',
    'fr-fr': 'Enregistrements {tablePreparation:string} retournés',
    'ru-ru': 'Возвращены записи {tablePreparation:string}',
    'uk-ua': 'Повернуто записів {tablePreparation:string}',
    'de-ch': 'Zurückgegebene {tablePreparation:string} Datensätze',
    'pt-br': 'Registros {tablePreparation:string} retornados',
    'hr-hr': 'Vraćeni zapisi {tablePreparation:string}',
    nb: 'Returnerte {tablePreparation:string} poster',
  },
  returnedAndSaved: {
    comment: 'Example: 2 Preparation records have been returned and saved',
    'en-us':
      '{count:number|formatted} {tablePreparation:string} records have been returned and saved',
    'es-es':
      '{count:number|formatted} {tablePreparation:string} Los registros han sido devueltos y guardados.',
    'fr-fr':
      'Les enregistrements {count:number|formatted} {tablePreparation:string} ont été renvoyés et sauvegardés.',
    'ru-ru':
      '{count:number|formatted} {tablePreparation:string} записи были возвращены и сохранены',
    'uk-ua':
      'Повернуто та збережено записів {count:number|formatted} {tablePreparation:string}',
    'de-ch':
      '{count:number|formatted} {tablePreparation:string} Datensätze wurden zurückgegeben und gespeichert',
    'pt-br':
      'Registros {count:number|formatted} {tablePreparation:string} foram retornados e salvos',
    'hr-hr':
      'Vraćeno je i spremljeno {count:number|formatted} {tablePreparation:string} zapisa',
    nb: '{count:number|formatted} {tablePreparation:string} poster er funnet og lagret',
  },
  deselectAll: {
    'en-us': 'Deselect all',
    'ru-ru': 'Снять выделение со всех элементов',
    'es-es': 'Deseleccionar todo',
    'fr-fr': 'Désélectionnez tout',
    'uk-ua': 'Зняти вибір усіх',
    'de-ch': 'Alle abwählen',
    'pt-br': 'Desmarque tudo',
    'hr-hr': 'Poništi odabir svih',
    nb: 'Fjern alle valgte',
  },
  available: {
    'en-us': 'Available',
    'ru-ru': 'Доступный',
    'es-es': 'Disponible',
    'fr-fr': 'Disponible',
    'uk-ua': 'Доступно',
    'de-ch': 'Verfügbar',
    'pt-br': 'Disponível',
    'hr-hr': 'Dostupno',
    nb: 'Tilgjengelig',
  },
  unavailable: {
    'en-us': 'Unavailable',
    'ru-ru': 'Недоступно',
    'es-es': 'Indisponible',
    'fr-fr': 'Indisponible',
    'uk-ua': 'Недоступно',
    'de-ch': 'Nicht verfügbar',
    'pt-br': 'Indisponível',
    'hr-hr': 'Nije dostupno',
    nb: 'Utilgjengelig',
  },
  returnLoan: {
    comment: 'Example: Return Loan records',
    'en-us': 'Return {tableLoan:string} records',
    'es-es': 'Devolver registros {tableLoan:string}',
    'fr-fr': 'Retourner les enregistrements {tableLoan:string}',
    'ru-ru': 'Возвращать записи {tableLoan:string}',
    'uk-ua': 'Повернути записи {tableLoan:string}',
    'de-ch': '{tableLoan:string} Datensätze zurückgeben',
    'pt-br': 'Retornar registros {tableLoan:string}',
    'hr-hr': 'Vrati {tableLoan:string} zapise',
    nb: 'Returner {tableLoan:string}-poster',
  },
  createRecord: {
    comment: 'Example: Create a Loan',
    'en-us': 'Create {table:string}',
    'es-es': 'Crear {table:string}',
    'fr-fr': 'Créer {table:string}',
    'ru-ru': 'Создать {table:string}',
    'uk-ua': 'Створити {table:string}',
    'de-ch': 'Erstelle {table:string}',
    'pt-br': 'Criar {table:string}',
    'hr-hr': 'Stvori {table:string}',
    nb: 'Opprett {table:string}',
  },
  noPreparationsWarning: {
    'en-us':
      'None of these records have preparations. Would you like to continue?',
    'de-ch':
      'Keiner dieser Datensätze enthält Vorbereitungen. Möchten Sie fortfahren?',
    'es-es': 'Ninguno de estos discos tiene preparativos. ¿Desea continuar?',
    'fr-fr':
      "Aucun de ces enregistrements n'a de préparation. Souhaitez-vous continuer ?",
    'pt-br': 'Nenhum desses registros possui preparativos. Deseja continuar?',
    'ru-ru': 'Ни одна из этих записей не была подготовлена. Хотите продолжить?',
    'uk-ua': 'Жоден із цих записів не має підготовки. Бажаєте продовжити?',
    'hr-hr': 'Nijedan od ovih zapisa nema pripreme. Želite li nastaviti?',
    nb: 'Ingen av disse postene har forberedelser. Vil du fortsette?',
  },
  continue: {
    'en-us': 'Continue',
    'de-ch': 'Weitermachen',
    'es-es': 'Continuar',
    'fr-fr': 'Continuer',
    'ru-ru': 'Продолжать',
    'uk-ua': 'Продовжити',
    'pt-br': 'Continuar',
    'hr-hr': 'Nastaviti',
    nb: 'Fortsett',
  },
  notAvailable: {
    'en-us': 'Not available',
    'de-ch': 'Nicht verfügbar',
    'es-es': 'No disponible',
    'fr-fr': 'Pas disponible',
    'pt-br': 'Não disponível',
    'ru-ru': 'Нет в наличии',
    'uk-ua': 'Не доступно',
    'hr-hr': 'Nije dostupno',
    nb: 'Ikke tilgjengelig',
  },
} as const);
